/**
 * Tensnet Predictive Search (Shopify AJAX API)
 *
 * Usage (example):
 * <predictive-search data-limit="8" data-types="product,query" data-limit-scope="each">
 *   <form action="/search" method="get" role="search">
 *     <input type="search" name="q" ...>
 *     <div class="predictive-search__panel" hidden></div>
 *   </form>
 * </predictive-search>
 */
(() => {
  if (customElements.get('predictive-search')) return;

  const DEFAULT_LIMIT = 6;
  const DEFAULT_TYPES = 'product,query';

  const escapeHtml = (str) =>
    String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  const debounce = (fn, wait = 200) => {
    let t;
    return (...args) => {
      window.clearTimeout(t);
      t = window.setTimeout(() => fn(...args), wait);
    };
  };

  let predictiveSearchBackdropEl = null;
  function ensurePredictiveSearchBackdrop() {
    if (predictiveSearchBackdropEl && predictiveSearchBackdropEl.parentNode) return;
    const el = document.createElement('div');
    el.className = 'predictive-search-backdrop';
    el.setAttribute('aria-hidden', 'true');
    document.body.appendChild(el);
    predictiveSearchBackdropEl = el;
  }

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this._input =
        this.querySelector('input[data-search-visible-input]') ||
        this.querySelector('input[type="search"]') ||
        this.querySelector('input[type="text"]');
      this._panel =
        this.querySelector('.predictive-search__panel') ||
        this.querySelector('[data-predictive-search-panel]');

      if (!this._input || !this._panel) return;

      this._form = this._input.closest('form');
      if (!this._form) return;
      this._queryInput =
        this._form.querySelector('input[type="hidden"][name="q"][data-search-query-hidden]') ||
        this._form.querySelector('input[type="hidden"][name="q"]');
      if (!this._queryInput) {
        this._queryInput = document.createElement('input');
        this._queryInput.type = 'hidden';
        this._queryInput.name = 'q';
        this._queryInput.setAttribute('data-search-query-hidden', 'true');
        this._form.appendChild(this._queryInput);
      }

      this._abortController = null;
      this._skuCache = this._skuCache || {};
      this._activeIndex = -1;
      this._isHeaderFullscreen = this.classList.contains('predictive-search--header');
      this._fullscreenListenersAttached = false;
      this._panelPortaled = false;
      this._panelAnchor = null;
      this._renamedTargetBayIds = this._renamedTargetBayIds || [];
      this._targetBayStarsCache = this._targetBayStarsCache || {};
      this._targetBayObserver = null;
      this._onResizeForFullscreen = () => {
        if (this._isHeaderFullscreen && !this._panel.hidden) this._updateHeaderPanelTop();
      };

      this._types = (this.getAttribute('data-types') || DEFAULT_TYPES).trim();
      this._soldOutText = (this.getAttribute('data-soldout-text') || 'Sold out').trim() || 'Sold out';
      this._limit = Number(this.getAttribute('data-limit') || DEFAULT_LIMIT);
      if (!Number.isFinite(this._limit) || this._limit <= 0) this._limit = DEFAULT_LIMIT;
      // Predictive Search API allows 1–10 per request; clamp for valid URLs.
      this._limit = Math.min(10, Math.max(1, Math.floor(this._limit)));
      // Default `each` so `limit` applies per resource type. With `all`, product + query share one pool (e.g. only 4 products when limit is 8).
      const scopeAttr = (this.getAttribute('data-limit-scope') || 'each').trim().toLowerCase();
      this._limitScope = scopeAttr === 'all' ? 'all' : 'each';
      this._staticSuggestions = (this.getAttribute('data-suggestions') || '')
        .split('|')
        .map((s) => s.trim())
        .filter(Boolean);

      // Accessibility wiring
      this._panel.id = this._panel.id || `predictive-search-panel-${Math.random().toString(36).slice(2)}`;
      this._input.setAttribute('autocomplete', 'off');
      this._input.setAttribute('aria-autocomplete', 'list');
      this._input.setAttribute('aria-controls', this._panel.id);
      this._input.setAttribute('aria-expanded', 'false');
      this._input.setAttribute('role', 'combobox');

      this._panel.setAttribute('role', 'listbox');

      this._onInput = debounce(() => this._handleInput(), 200);
      this._onKeyDown = (e) => this._handleKeyDown(e);
      this._onFocus = () => this._handleInput({ allowEmpty: false, reopenIfHasResults: true });
      this._onSubmit = () => this._syncQueryInput();
      this._onDocClick = (e) => {
        var t = e.target;
        if (this.contains(t)) return;
        if (this._panelPortaled && this._panel && this._panel.contains(t)) return;
        this.close();
      };

      this._clearBtn = this.querySelector('button[type="reset"]');
      this._onClearClick = (e) => {
        e.preventDefault();
        this._input.value = '';
        this._syncQueryInput();
        this._abort();
        this._panel.innerHTML = '';
        this.close();
        this._input.focus();
      };


      this._input.addEventListener('input', this._onInput);
      this._input.addEventListener('keydown', this._onKeyDown);
      this._input.addEventListener('focus', this._onFocus);
      this._form.addEventListener('submit', this._onSubmit);
      document.addEventListener('click', this._onDocClick);
      if (this._clearBtn) {
        this._clearBtn.addEventListener('click', this._onClearClick);
      }
      this._syncQueryInput();
    }

    disconnectedCallback() {
      if (this._input) {
        this._input.removeEventListener('input', this._onInput);
        this._input.removeEventListener('keydown', this._onKeyDown);
        this._input.removeEventListener('focus', this._onFocus);
      }
      if (this._form) {
        this._form.removeEventListener('submit', this._onSubmit);
      }
      document.removeEventListener('click', this._onDocClick);
      if (this._clearBtn) {
        this._clearBtn.removeEventListener('click', this._onClearClick);
      }
      this._unportalPanel();
      if (this._targetBayObserver) {
        try { this._targetBayObserver.disconnect(); } catch (_) { /* TargetBay observer was already disconnected */ }
        this._targetBayObserver = null;
      }
      if (this._fullscreenListenersAttached) {
        window.removeEventListener('resize', this._onResizeForFullscreen);
        window.removeEventListener('scroll', this._onResizeForFullscreen);
        this._fullscreenListenersAttached = false;
      }
      if (this._isHeaderFullscreen) {
        document.documentElement.style.removeProperty('--predictive-search-panel-top');
      }
      this._abort();
      this._restoreTargetBayIds();
    }

    _portalPanelToBody() {
      if (!this._isHeaderFullscreen || !this._panel || this._panelPortaled) return;
      if (!this._panelAnchor) {
        this._panelAnchor = document.createComment('predictive-search-panel-anchor');
        this._panel.parentNode.insertBefore(this._panelAnchor, this._panel);
      }
      this._panel.classList.add('predictive-search__panel--fullscreen');
      document.body.appendChild(this._panel);
      this._panelPortaled = true;
    }

    _unportalPanel() {
      if (!this._panel || !this._panelPortaled) return;
      try {
        this._panel.classList.remove('predictive-search__panel--fullscreen');
        if (this._panelAnchor && this._panelAnchor.parentNode) {
          this._panelAnchor.parentNode.insertBefore(this._panel, this._panelAnchor);
        }
      } catch (_) { /* Panel could not be restored to its original anchor point */ }
      this._panelPortaled = false;
    }

    _updateHeaderPanelTop() {
      if (!this._isHeaderFullscreen) return;
      var anchor = (this._form && this._form.querySelector('.form-search')) || this._input;
      if (!anchor) return;
      var r = anchor.getBoundingClientRect();
      var topPx = Math.max(0, Math.ceil(r.bottom));
      document.documentElement.style.setProperty('--predictive-search-panel-top', topPx + 'px');
    }

    _syncQueryInput() {
      if (this._queryInput) {
        this._queryInput.value = this._input.value || '';
      }
    }

    open() {
      this._panel.hidden = false;
      this._input.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('predictive-search-open');
      document.body.classList.add('predictive-search-open');
      if (this._isHeaderFullscreen) {
        ensurePredictiveSearchBackdrop();
        document.documentElement.classList.add('predictive-search-header-open');
        document.body.classList.add('predictive-search-header-open');
        this._portalPanelToBody();
        this._updateHeaderPanelTop();
        if (!this._fullscreenListenersAttached) {
          window.addEventListener('resize', this._onResizeForFullscreen);
          window.addEventListener('scroll', this._onResizeForFullscreen, { passive: true });
          this._fullscreenListenersAttached = true;
        }
      }
    }

    close() {
      if (this._fullscreenListenersAttached) {
        window.removeEventListener('resize', this._onResizeForFullscreen);
        window.removeEventListener('scroll', this._onResizeForFullscreen);
        this._fullscreenListenersAttached = false;
      }
      if (this._isHeaderFullscreen) {
        document.documentElement.style.removeProperty('--predictive-search-panel-top');
        this._unportalPanel();
      }
      this._panel.hidden = true;
      this._input.setAttribute('aria-expanded', 'false');
      this._activeIndex = -1;
      var anyPanelOpen = Array.from(document.querySelectorAll('.predictive-search__panel')).some(function (p) {
        return !p.hidden;
      });
      if (!anyPanelOpen) {
        document.documentElement.classList.remove('predictive-search-open');
        document.body.classList.remove('predictive-search-open');
      }
      var anyHeaderPanelOpen = Array.from(
        document.querySelectorAll('predictive-search.predictive-search--header .predictive-search__panel')
      ).some(function (p) {
        return !p.hidden;
      });
      if (!anyHeaderPanelOpen) {
        document.documentElement.classList.remove('predictive-search-header-open');
        document.body.classList.remove('predictive-search-header-open');
        this._restoreTargetBayIds();
      }
    }

    _abort() {
      try {
        if (this._abortController) this._abortController.abort();
      } catch (_) { /* Abort signal could not be sent to the pending fetch request */ }
      this._abortController = null;
    }

    async _handleInput(opts = {}) {
      const { allowEmpty = false, reopenIfHasResults = false } = opts;
      const term = (this._input.value || '').trim();
      this._syncQueryInput();

      if (!allowEmpty && term.length < 2) {
        this._abort();
        this.close();
        return;
      }

      if (reopenIfHasResults && this._panel.innerHTML.trim() && this._panel.hidden) {
        this.open();
      }

      this._abort();
      this._abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;

      this.open();
      this._renderLoading(term);

      const url = this._buildUrl(term);

      try {
        const res = await fetch(url, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          signal: this._abortController ? this._abortController.signal : undefined
        });
        if (!res.ok) throw new Error(`Predictive search failed: ${res.status}`);
        const data = await res.json();
        await this._renderResults(term, data);
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        this._renderError(term);
      }
    }

    _buildUrl(term) {
      const params = new URLSearchParams();
      params.set('q', term);
      params.set('resources[type]', this._types);
      params.set('resources[limit]', String(this._limit));
      params.set('resources[limit_scope]', this._limitScope);
      // Keep behavior aligned with existing forms
      params.set('resources[options][unavailable_products]', 'show');
      params.set(
        'resources[options][fields]',
        'title,product_type,variants.title,vendor,variants.sku'
      );
      return `/search/suggest.json?${params.toString()}`;
    }

    _buildCardsUrl(term) {
      const params = new URLSearchParams();
      params.set('view', 'predictive-cards');
      params.set('type', 'product');
      params.set('q', term);
      params.set('options[unavailable_products]', 'show');
      params.set('options[prefix]', 'last');
      params.set('limit', String(this._limit));
      return `/search?${params.toString()}`;
    }

    _runTargetBayRefresh(root) {
      try {
        const tbFns = [
          'tbBulkReviewInit',
          'tbBulkReviewRender',
          'targetbayBulkReview',
          'TB_bulkReviewRender',
          'tbsiteBulkReview',
          'tbInitBulkReviews',
          'tbRenderProductStars',
          'reRenderTargetbayStars'
        ];
        for (let i = 0; i < tbFns.length; i++) {
          const fn = window[tbFns[i]];
          if (typeof fn === 'function') {
            try { fn(root || document); } catch (_) { /* A TargetBay callback function threw an error during execution */ }
          }
        }
      } catch (_) { /* TargetBay global functions initialization failed */ }
      try {
        document.dispatchEvent(
          new CustomEvent('tensnet:targetbay-refresh', { detail: { root: root || null } })
        );
      } catch (_) { /* Custom refresh event dispatch failed */ }
      try {
        if (window.jQuery) {
          window.jQuery(document).trigger('shopify:section:load', [root || document]);
        }
      } catch (_) { /* jQuery theme section load trigger failed */ }
    }

    _getTargetBayProductId(el) {
      if (!el) return '';
      return String(el.getAttribute('data-product-id') || el.getAttribute('id') || '').trim();
    }

    _prefillTargetBayStars(root) {
      if (!root) return;
      const containers = Array.from(root.querySelectorAll('.targetbay_star_container'));
      containers.forEach((el) => {
        const pid = this._getTargetBayProductId(el);
        if (!pid) return;
        const cached = this._targetBayStarsCache[pid];
        if (cached && !String(el.innerHTML || '').trim()) {
          el.innerHTML = cached;
        }
      });
    }

    _cacheTargetBayStarsFrom(root) {
      if (!root) return;
      const containers = Array.from(root.querySelectorAll('.targetbay_star_container'));
      containers.forEach((el) => {
        const pid = this._getTargetBayProductId(el);
        if (!pid) return;
        const html = String(el.innerHTML || '').trim();
        if (html) this._targetBayStarsCache[pid] = html;
      });
    }

    _observeTargetBayStars(root) {
      if (!root || typeof MutationObserver === 'undefined') return;
      if (this._targetBayObserver) {
        try { this._targetBayObserver.disconnect(); } catch (_) { /* TargetBay observer was already disconnected */ }
        this._targetBayObserver = null;
      }
      this._targetBayObserver = new MutationObserver(() => {
        this._cacheTargetBayStarsFrom(root);
      });
      this._targetBayObserver.observe(root, { childList: true, subtree: true, characterData: true });
    }

    _restoreTargetBayIds() {
      if (!this._renamedTargetBayIds || !this._renamedTargetBayIds.length) return;
      this._renamedTargetBayIds.forEach((r) => {
        try { r.el.setAttribute('id', r.id); } catch (_) { /* Element ID could not be restored to its original value */ }
      });
      this._renamedTargetBayIds = [];
    }

    _prepareTargetBayContainers(root) {
      if (!root) return function () {};

      const localStars = Array.from(root.querySelectorAll('.targetbay_star_container'));
      const idsNeeded = [];
      localStars.forEach((el) => {
        const pid =
          String(el.getAttribute('data-product-id') || el.getAttribute('id') || '').trim();
        if (!pid) return;
        el.setAttribute('data-product-id', pid);
        el.setAttribute('id', pid);
        idsNeeded.push(pid);
      });
      if (!idsNeeded.length) return function () {};

      const idSet = new Set(idsNeeded);
      const allStars = Array.from(document.querySelectorAll('.targetbay_star_container[id]'));
      const alreadyRenamed = new WeakSet(
        (this._renamedTargetBayIds || []).map((x) => x.el)
      );
      allStars.forEach((el, idx) => {
        if (root.contains(el)) return;
        if (alreadyRenamed.has(el)) return;
        const id = String(el.getAttribute('id') || '').trim();
        if (!id || id.indexOf('tb-temp-') === 0) return;
        if (!idSet.has(id)) return;
        const tempId = `tb-temp-${id}-${Date.now()}-${idx}`;
        this._renamedTargetBayIds.push({ el: el, id: id });
        el.setAttribute('id', tempId);
      });
      return function () {};
    }

    _refreshWidgetsAfterRender(root) {
      this._prepareTargetBayContainers(root);
      this._prefillTargetBayStars(root);
      const run = () => {
        if (typeof window.tensnetRefreshProductWidgets === 'function') {
          window.tensnetRefreshProductWidgets(root);
        }
        this._runTargetBayRefresh(root);
        this._cacheTargetBayStarsFrom(root);
      };
      run();
      window.setTimeout(run, 250);
      window.setTimeout(run, 900);
      this._observeTargetBayStars(root);
    }

    _extractProductSku(product) {
      if (!product) return '';
      const direct = String(product.sku || '').trim();
      if (direct) return direct;
      const variants = product.variants;
      if (!Array.isArray(variants)) return '';
      for (let i = 0; i < variants.length; i++) {
        const s = String(variants[i]?.sku || '').trim();
        if (s) return s;
      }
      return '';
    }

    _getProductsFromResponse(data) {
      // Shopify response shape: resources.results.products[]
      const products = data?.resources?.results?.products;
      return Array.isArray(products) ? products : [];
    }

    _getQueriesFromResponse(data) {
      const queries = data?.resources?.results?.queries;
      return Array.isArray(queries) ? queries : [];
    }

    _getProductsCountFromResponse(data) {
      const count = data?.resources?.results_count?.products;
      return Number.isFinite(Number(count)) ? Number(count) : null;
    }

    _formatMoney(cents) {
      const value = Number(cents);
      if (!Number.isFinite(value)) return '';
      try {
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          return window.Shopify.formatMoney(value, window.Shopify.money_format);
        }
      } catch (_) { /* Shopify formatter failed, fall back to standard formatting */ }
      // Fallback: USD-style formatting (best effort)
      return `$${(value / 100).toFixed(2)}`;
    }

    /** Predictive Search JSON uses decimal currency amounts (e.g. 19.99), not cents. */
    _formatMoneyFromDecimal(amount) {
      const n = Number(amount);
      if (!Number.isFinite(n)) return '';
      const cents = Math.round(n * 100 + Number.EPSILON);
      return this._formatMoney(cents);
    }

    _renderLoading(term) {
      this._panel.innerHTML = `
        <div class="predictive-search__status" role="status" aria-live="polite">
          Searching for <strong>${escapeHtml(term)}</strong>…
        </div>
      `;
    }

    _renderError(term) {
      this._panel.innerHTML = `
        <div class="predictive-search__status" role="status" aria-live="polite">
          Couldn’t load suggestions for <strong>${escapeHtml(term)}</strong>.
          <a class="predictive-search__view-all" href="/search?type=product&q=${encodeURIComponent(term)}">Search instead</a>
        </div>
      `;
    }

    async _renderResults(term, data) {
      const rawProducts = this._getProductsFromResponse(data);
      const sortedProducts = [...rawProducts].sort((a, b) => {
        const aAvail = a.available !== false;
        const bAvail = b.available !== false;
        if (aAvail && !bAvail) return -1;
        if (!aAvail && bAvail) return 1;
        return 0;
      });
      const products = sortedProducts.slice(0, this._limit);
      const queries = this._getQueriesFromResponse(data);
      const productsCount = this._getProductsCountFromResponse(data);

      const viewAllUrl = `/search?type=product&q=${encodeURIComponent(term)}`;

      const suggestionTerms = [
        ...queries.map((q) => q?.text).filter(Boolean),
        ...this._staticSuggestions
      ]
        .filter((v, i, arr) => arr.indexOf(v) === i)
        .slice(0, 8);

      const suggestionHtml = suggestionTerms.length
        ? suggestionTerms
          .map((s) => {
            const label = escapeHtml(s);
            const href = `/search?type=product&q=${encodeURIComponent(s)}`;
            return `
                <a class="predictive-search__suggestion" href="${href}" data-suggest="${label}">
                  <span class="predictive-search__suggestion-icon" aria-hidden="true">⌕</span>
                  <span class="predictive-search__suggestion-text">${label}</span>
                </a>
              `;
          })
          .join('')
        : '<div class="predictive-search__empty">No suggestions</div>';

      const productCards = products.map((p, idx) => {
        const titleText = escapeHtml(p?.title || '');
        const url = escapeHtml(p?.url || '#');
        const rawImg = p?.featured_image?.url || p?.image || p?.featured_image;
        const imgUrl = rawImg ? escapeHtml(rawImg) : '';
        const sku = this._extractProductSku(p);
        const handle = String(p?.handle || '').trim();
        const skuHtml = sku
          ? `<div class="product-sku">SKU:&nbsp;${escapeHtml(sku)}</div>`
          : `<div class="product-sku" data-sku-handle="${escapeHtml(handle)}"></div>`;
        const pid = p?.id != null ? escapeHtml(String(p.id)) : '';
        const isSoldOut = p?.available === false;
        const soldOutHtml = isSoldOut
          ? `<span class="sold-out-badge" id="buy-now" disabled="disabled">${escapeHtml(this._soldOutText)}</span>`
          : '';

        const priceMin = Number(p?.price_min ?? p?.price);
        const compareMax = Number(p?.compare_at_price_max);
        let priceInner = '';
        if (Number.isFinite(priceMin)) {
          priceInner = `<span class="new-price">${escapeHtml(this._formatMoneyFromDecimal(priceMin))}</span>`;
          if (Number.isFinite(compareMax) && compareMax > priceMin) {
            priceInner += `<span class="old-price">${escapeHtml(this._formatMoneyFromDecimal(compareMax))}</span>`;
          }
        }

        const imgBlock = imgUrl
          ? `<div class="predictive-search__prod-img"><img src="${imgUrl}" alt="${titleText}" loading="lazy"></div>`
          : '<div class="predictive-search__prod-img predictive-search__prod-img--empty"></div>';

        return `
          <div class="predictive-search__product single-product-wrap" role="option" data-index="${idx}" data-product-url="${url}">
            <a class="predictive-search__product-media product-image pro-img" href="${url}">
              ${imgBlock}
            </a>
            <div class="predictive-search__prod-content product-content">
              <div class="product-title">
                <a href="${url}">${titleText}</a>
              </div>
              ${skuHtml}
              <div class="targetbay_star_container" id="${pid}" data-product-id="${pid}"></div>
              ${
  priceInner
    ? `<div class="price-box">${priceInner}</div>`
    : ''
  }
            </div>
            ${soldOutHtml}
          </div>
        `;
      }).join('');
      let renderedProductCards = productCards;
      if (products.length) {
        try {
          const cardsRes = await fetch(this._buildCardsUrl(term), {
            method: 'GET',
            headers: { Accept: 'text/html' },
            signal: this._abortController ? this._abortController.signal : undefined
          });
          if (cardsRes.ok) {
            const cardsHtml = (await cardsRes.text()).trim();
            if (cardsHtml) renderedProductCards = cardsHtml;
          }
        } catch (cardsErr) {
          if (!(cardsErr && cardsErr.name === 'AbortError')) {
            // Keep JSON fallback cards so predictive search remains functional.
          }
        }
      }
      const productsHtml = products.length
        ? `<div class="predictive-search__products-grid" role="presentation">${renderedProductCards}</div>`
        : '<div class="predictive-search__empty">No products found</div>';

      const viewAllLabel =
        productsCount && Number.isFinite(productsCount)
          ? `View all ${productsCount} items`
          : 'View all results';

      this._panel.innerHTML = `
        <div class="predictive-search__layout" role="presentation">
          <div class="predictive-search__left" role="presentation">
            <div class="predictive-search__heading">Popular suggestions</div>
            <div class="predictive-search__suggestions" role="presentation">
              ${suggestionHtml}
            </div>
            <a class="predictive-search__viewall-btn predictive-search__viewall-btn--left" href="${viewAllUrl}">
              ${escapeHtml(viewAllLabel)}
            </a>
          </div>
          <div class="predictive-search__right" role="presentation">
            <div class="predictive-search__heading predictive-search__heading--right">Products</div>
            ${productsHtml}
          </div>
        </div>
        <div class="predictive-search__footer" role="presentation">
          <a class="predictive-search__viewall-btn" href="${viewAllUrl}">${escapeHtml(viewAllLabel)}</a>
        </div>
      `;

      // Clicking a suggestion should populate the input and submit.
      this._panel.querySelectorAll('[data-suggest]').forEach((a) => {
        a.addEventListener('click', (e) => {
          e.preventDefault();
          const v = a.getAttribute('data-suggest') || '';
          this._input.value = v;
          this._syncQueryInput();
          this._form.requestSubmit ? this._form.requestSubmit() : this._form.submit();
        });
      });

      this._activeIndex = -1;

      this._hydrateMissingSkus();

      if (this._isHeaderFullscreen) {
        window.requestAnimationFrame(() => this._updateHeaderPanelTop());
      }
      if (typeof window.tensnetApplyGridDiscountLabels === 'function') {
        window.tensnetApplyGridDiscountLabels();
      }
      this._refreshWidgetsAfterRender(this._panel);
      if (typeof tbrForm !== 'undefined' && typeof tbrForm.init === 'function') {
        try { tbrForm.init(); } catch (_) { /* tbrForm initialization failed */ }
      }
      window.SmartWishlistMain();
    }

    _hydrateMissingSkus() {
      const slots = Array.from(this._panel.querySelectorAll('.product-sku[data-sku-handle]'));
      if (!slots.length) return;
      slots.forEach((slot) => {
        const handle = slot.getAttribute('data-sku-handle');
        if (!handle) return;
        if (this._skuCache && typeof this._skuCache[handle] !== 'undefined') {
          const cached = this._skuCache[handle];
          slot.innerHTML = cached ? `SKU:&nbsp;${escapeHtml(cached)}` : '';
          slot.removeAttribute('data-sku-handle');
          return;
        }
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const url = `${root}products/${encodeURIComponent(handle)}.js`;
        fetch(url, { method: 'GET', headers: { Accept: 'application/json' } })
          .then((res) => (res.ok ? res.json() : null))
          .then((product) => {
            let resolvedSku = '';
            if (product && Array.isArray(product.variants)) {
              for (let i = 0; i < product.variants.length; i++) {
                const s = String(product.variants[i]?.sku || '').trim();
                if (s) {
                  resolvedSku = s;
                  break;
                }
              }
            }
            this._skuCache[handle] = resolvedSku;
            slot.innerHTML = resolvedSku ? `SKU:&nbsp;${escapeHtml(resolvedSku)}` : '';
            slot.removeAttribute('data-sku-handle');
          })
          .catch(() => {
            this._skuCache[handle] = '';
            slot.removeAttribute('data-sku-handle');
          });
      });
    }

    _handleKeyDown(e) {
      if (this._panel.hidden) return;

      const items = Array.from(this._panel.querySelectorAll('.predictive-search__product'));
      if (!items.length) {
        if (e.key === 'Escape') {
          this.close();
          this._input.blur();
        }
        return;
      }

      const setActive = (nextIdx) => {
        this._activeIndex = nextIdx;
        items.forEach((el, i) => el.classList.toggle('is-active', i === nextIdx));
        const active = items[nextIdx];
        if (active) active.scrollIntoView({ block: 'nearest' });
      };

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          const next = Math.min(items.length - 1, this._activeIndex + 1);
          setActive(next);
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          const next = Math.max(0, this._activeIndex - 1);
          setActive(next);
          break;
        }
        case 'Enter': {
          if (this._activeIndex >= 0 && items[this._activeIndex]) {
            e.preventDefault();
            const row = items[this._activeIndex];
            const titleLink = row.querySelector('.product-title a');
            if (titleLink && titleLink.getAttribute('href')) {
              window.location.assign(titleLink.href);
            } else {
              const u = row.getAttribute('data-product-url');
              if (u) window.location.assign(u);
            }
          }
          break;
        }
        case 'Escape': {
          e.preventDefault();
          this.close();
          this._input.blur();
          break;
        }
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();

